/* md.js — Maison Dalia Couture interactions (Vitrine Noir layer).
   GSAP (+ScrollTrigger) is loaded as classic deferred scripts before this file; three.js is imported lazily only when the
   hero scrolls into view, WebGL works and the visitor has not asked for reduced motion.
   Everything degrades: no GSAP -> content simply shows; no WebGL -> the static photo card stays.
   Smoothness rules (Ninos 2026-10-26): reveals are CSS transitions flipped by IntersectionObserver, nothing is scrubbed to
   scroll, no backdrop blur, the WebGL loop pauses off-screen, pixel ratio capped at 1.5. */
(function () {
  'use strict';
  var doc = document, root = doc.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var g = window.gsap, ST = window.ScrollTrigger;
  var hasGsap = !!(g && ST) && !reduced;
  if (hasGsap) { g.registerPlugin(ST); }

  /* ---------------- Reveals v2 ---------------- */
  function markReveals(scope) {
    var sel = [
      '.pp-section .pp-eyebrow', '.pp-section .pp-h2', '.pp-section .pp-sub', '.pp-section .pp-text', '.pp-section .pp-cta',
      '.vc-card', '.pp-gb', '.pp-faq__item', '.pp-guarantee__card', '.pp-media', '.vc-results__row', '.vc-results__kicker',
      '.vc-results__text', '.vc-results__caption', '.pp-pct', '.testimonial-card', '.collection .card-wrapper',
      '.collection-title', '.comparison-table-container', '.content-and-comparison-table .content-container',
      '.md-look', '.md-runway__head', '.md-fit__eyebrow', '.md-fit__h', '.md-fit__text', '.md-fit__cta', '.md-fit__step', '.md-fit__note',
      '.newsletter__wrapper > *'
    ].join(',');
    var nodes = (scope || doc).querySelectorAll(sel);
    nodes.forEach(function (n) {
      if (n.closest('.md-hero')) return;
      if (!n.hasAttribute('data-md-reveal')) n.setAttribute('data-md-reveal', '');
    });
    return nodes;
  }
  function runReveals() {
    if (reduced || !('IntersectionObserver' in window)) return;
    var vh = window.innerHeight || 800, list = [];
    markReveals().forEach(function (n) {
      if (n.getBoundingClientRect().top > vh) list.push(n); else n.removeAttribute('data-md-reveal');
    });
    if (!list.length) return;
    root.classList.add('md-reveal');
    var io = new IntersectionObserver(function (entries) {
      var k = 0;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = e.target; io.unobserve(n);
        n.style.transitionDelay = Math.min(k++, 8) * 60 + 'ms';
        n.addEventListener('transitionend', function done() { n.classList.add('md-done'); n.style.transitionDelay = ''; n.removeEventListener('transitionend', done); });
        n.classList.add('md-in');
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    list.forEach(function (n) { io.observe(n); });
    setTimeout(function () { list.forEach(function (n) { if (!n.classList.contains('md-in')) { n.classList.add('md-in', 'md-done'); } }); }, 8000);
  }

  /* ---------------- Percent rings count up ---------------- */
  function runRings() {
    if (!hasGsap) return;
    doc.querySelectorAll('.pp-pct__ring').forEach(function (ring) {
      var span = ring.querySelector('span'); if (!span) return;
      var target = parseFloat(ring.style.getPropertyValue('--pp-value')) || parseFloat(span.textContent) || 0;
      var obj = { v: 0 };
      ring.style.setProperty('--pp-value', '0'); span.textContent = '0%';
      ST.create({
        trigger: ring, start: 'top 90%', once: true,
        onEnter: function () {
          g.to(obj, { v: target, duration: 1.6, ease: 'power2.out', onUpdate: function () {
            ring.style.setProperty('--pp-value', obj.v.toFixed(1)); span.textContent = Math.round(obj.v) + '%';
          } });
        }
      });
    });
  }

  /* ---------------- Tilt + magnetic (fine pointers only) ---------------- */
  function runTilt() {
    if (!hasGsap || !finePointer) return;
    doc.querySelectorAll('.collection .card-wrapper .card, .vc-cards--tiles .vc-card, .pp-gb, .md-look').forEach(function (el) {
      el.classList.add('md-tilt');
      var rx = g.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3' });
      var ry = g.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3' });
      g.set(el, { transformPerspective: 900 });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        rx(-py * 5); ry(px * 7);
      });
      el.addEventListener('pointerleave', function () { rx(0); ry(0); });
    });
    doc.querySelectorAll('.button--primary, .md-hero__actions .button, .md-fit__cta .button').forEach(function (b) {
      var x = g.quickTo(b, 'x', { duration: 0.4, ease: 'power3' }), y = g.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        x(((e.clientX - r.left) / r.width - 0.5) * 8); y(((e.clientY - r.top) / r.height - 0.5) * 6);
      });
      b.addEventListener('pointerleave', function () { x(0); y(0); });
    });
  }

  /* ---------------- Runway lookbook: arrows + drag ---------------- */
  function runRunway() {
    doc.querySelectorAll('[data-md-runway]').forEach(function (sec) {
      var track = sec.querySelector('[data-md-track]'); if (!track) return;
      function step() { var c = track.querySelector('.md-look'); return c ? c.getBoundingClientRect().width + 16 : 320; }
      function go(dir) {
        var to = track.scrollLeft + dir * step() * 2;
        if (hasGsap) g.to(track, { scrollLeft: to, duration: 0.7, ease: 'power3.out' }); else track.scrollTo({ left: to, behavior: 'smooth' });
      }
      var prev = sec.querySelector('[data-md-prev]'), next = sec.querySelector('[data-md-next]');
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      if (!finePointer) return;
      var down = false, sx = 0, sl = 0, moved = 0;
      track.addEventListener('pointerdown', function (e) { down = true; moved = 0; sx = e.clientX; sl = track.scrollLeft; track.classList.add('is-drag'); });
      track.addEventListener('pointermove', function (e) { if (!down) return; var dx = e.clientX - sx; moved += Math.abs(dx); track.scrollLeft = sl - dx; });
      function up() { if (!down) return; down = false; track.classList.remove('is-drag'); }
      track.addEventListener('pointerup', up); track.addEventListener('pointercancel', up); track.addEventListener('pointerleave', up);
      track.addEventListener('click', function (e) { if (moved > 8) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
  }

  /* ---------------- Hero copy ---------------- */
  function runHeroCopy(hero) {
    var h = hero.querySelector('.md-hero__h');
    if (h && !h.querySelector('.md-w')) {
      h.innerHTML = h.innerHTML.replace(/(<em>.*?<\/em>|[^\s<]+)(\s*)/g, function (_, w, sp) { return '<span class="md-w">' + w + (sp ? ' ' : '') + '</span>'; });
    }
    if (!hasGsap) return;
    var tl = g.timeline({ defaults: { ease: 'power3.out' } });
    tl.from(hero.querySelectorAll('.pp-eyebrow'), { y: 14, opacity: 0, duration: 0.7 }, 0.05)
      .from(hero.querySelectorAll('.md-w'), { yPercent: 60, opacity: 0, duration: 0.9, stagger: 0.07 }, 0.1)
      .from(hero.querySelectorAll('.md-hero__lead'), { y: 16, opacity: 0, duration: 0.8 }, 0.5)
      .from(hero.querySelectorAll('.md-hero__actions .button'), { y: 12, opacity: 0, duration: 0.6, stagger: 0.1 }, 0.65)
      .from(hero.querySelectorAll('.md-hero__metric'), { y: 16, opacity: 0, duration: 0.7, stagger: 0.08 }, 0.8)
      .from(hero.querySelectorAll('.md-hero__look, .md-hero__tag, .md-hero__hint'), { y: 10, opacity: 0, duration: 0.6 }, 0.9);
    // Insurance: nothing stays at opacity 0 if a tween is interrupted (theme editor, tab switch)
    setTimeout(function () { hero.querySelectorAll('.md-w, .pp-eyebrow, .md-hero__lead, .button, .md-hero__metric, .md-hero__look, .md-hero__tag').forEach(function (n) { n.style.opacity = ''; n.style.transform = ''; }); }, 2600);
  }

  /* ---------------- Hero: three.js atelier stage ---------------- */
  function webglOk() {
    try { var c = doc.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; }
  }
  // Shopify minifies theme JS and rewrites a dynamic import() into require(), so the ES module is pulled in by an
  // inline <script type="module"> instead and handed over on window.
  function loadThree(src) {
    return new Promise(function (resolve, reject) {
      if (window.__mdThree) return resolve(window.__mdThree);
      window.addEventListener('md:three', function () { resolve(window.__mdThree); }, { once: true });
      var s = doc.createElement('script'); s.type = 'module';
      s.textContent = "import * as T from '" + src + "'; window.__mdThree = T; window.dispatchEvent(new Event('md:three'));";
      doc.head.appendChild(s);
      setTimeout(function () { if (!window.__mdThree) reject(new Error('three.js did not load')); }, 15000);
    });
  }
  function initHero3D(hero) {
    var canvas = hero.querySelector('.md-hero__canvas');
    if (!canvas || reduced || !webglOk()) return;
    var src = hero.getAttribute('data-three') || 'https://cdnjs.cloudflare.com/ajax/libs/three.js/0.160.0/three.module.min.js';
    loadThree(src).then(function (THREE) { buildHero(THREE, hero, canvas); }).catch(function (err) { if (window.console) console.warn('md hero 3d skipped', err); });
  }

  var SILK_VERT = [
    'uniform float uTime;',
    'varying vec3 vNormal; varying vec3 vPos; varying vec2 vUv;',
    'float wave(vec2 p, float t){',
    '  return 0.34*sin(p.x*1.15 + t*0.55) + 0.22*sin(p.y*1.6 + p.x*0.6 - t*0.42) + 0.12*sin((p.x+p.y)*2.6 + t*0.9);',
    '}',
    'void main(){',
    '  vUv = uv;',
    '  vec3 p = position;',
    '  float e = 0.02;',
    '  float z = wave(p.xy, uTime);',
    '  float zx = wave(p.xy + vec2(e,0.0), uTime);',
    '  float zy = wave(p.xy + vec2(0.0,e), uTime);',
    '  p.z += z;',
    '  vec3 n = normalize(vec3(-(zx-z)/e, -(zy-z)/e, 1.0));',
    '  vNormal = normalize(normalMatrix * n);',
    '  vec4 mv = modelViewMatrix * vec4(p, 1.0);',
    '  vPos = mv.xyz;',
    '  gl_Position = projectionMatrix * mv;',
    '}'
  ].join('\n');
  var SILK_FRAG = [
    'uniform vec3 uBase; uniform vec3 uShade; uniform vec3 uLight; uniform float uOpacity;',
    'varying vec3 vNormal; varying vec3 vPos; varying vec2 vUv;',
    'void main(){',
    '  vec3 n = normalize(vNormal);',
    '  vec3 l = normalize(vec3(-0.5, 0.8, 1.0));',
    '  vec3 v = normalize(-vPos);',
    '  float diff = clamp(dot(n, l), 0.0, 1.0);',
    '  vec3 h = normalize(l + v);',
    '  float spec = pow(clamp(dot(n, h), 0.0, 1.0), 42.0);',
    '  float fres = pow(1.0 - clamp(dot(n, v), 0.0, 1.0), 2.5);',
    '  vec3 col = mix(uShade, uBase, 0.35 + 0.65*diff);',
    '  col += uLight * (spec*0.55 + fres*0.35);',
    '  float edge = smoothstep(0.0, 0.3, vUv.x) * smoothstep(0.0, 0.3, 1.0-vUv.x) * smoothstep(0.0, 0.26, vUv.y) * smoothstep(0.0, 0.3, 1.0-vUv.y);',
    '  gl_FragColor = vec4(col, uOpacity * edge);',
    '}'
  ].join('\n');

  function buildHero(THREE, hero, canvas) {
    var stage = hero.querySelector('.md-hero__stage');
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var looksEl = hero.querySelector('[data-md-looks]');
    var looks = [];
    try { looks = JSON.parse(looksEl ? looksEl.textContent : '[]'); } catch (e) { looks = []; }
    if (!looks.length) return;
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
    camera.position.set(0, 0.3, 7.6); camera.lookAt(0, 0.1, 0);

    // Silk curtain
    var silkMat = new THREE.ShaderMaterial({
      vertexShader: SILK_VERT, fragmentShader: SILK_FRAG, transparent: true, depthWrite: false, side: THREE.DoubleSide,
      uniforms: { uTime: { value: 0 }, uBase: { value: new THREE.Color(0xe9dcbf) }, uShade: { value: new THREE.Color(0xb89a68) }, uLight: { value: new THREE.Color(0xfff6e4) }, uOpacity: { value: 0 } }
    });
    var silk = new THREE.Mesh(new THREE.PlaneGeometry(6.4, 6.2, 80, 72), silkMat);
    silk.position.set(0.5, 0.1, -1.6); silk.rotation.set(0.06, -0.2, -0.1); scene.add(silk);

    // Gown card: white mat + photo, floating in front of the silk
    var card = new THREE.Group(); card.position.set(0, 0.05, 1.1); scene.add(card);
    var matWhite = new THREE.MeshBasicMaterial({ color: 0xffffff });
    var matInk = new THREE.MeshBasicMaterial({ color: 0x0a0a0a });
    var CW = 2.3, CH = 2.95;
    var inkFrame = new THREE.Mesh(new THREE.PlaneGeometry(CW + 0.04, CH + 0.04), matInk); inkFrame.position.z = -0.012; card.add(inkFrame);
    var mat = new THREE.Mesh(new THREE.PlaneGeometry(CW + 0.02, CH + 0.02), matWhite); mat.position.z = -0.006; card.add(mat);
    var back = new THREE.Mesh(new THREE.PlaneGeometry(CW + 0.04, CH + 0.04), matWhite); back.rotation.y = Math.PI; back.position.z = -0.014; card.add(back);
    var photoMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
    var photo = new THREE.Mesh(new THREE.PlaneGeometry(CW - 0.16, CH - 0.16), photoMat); card.add(photo);
    var textures = [], loader = new THREE.TextureLoader(); loader.setCrossOrigin('anonymous');
    function texFor(i, cb) {
      if (textures[i]) return cb(textures[i]);
      loader.load(looks[i].src, function (t) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy()); textures[i] = t; cb(t); });
    }
    function fitTexture(t) {
      // cover-fit the photo onto the card plane (card is 4:5-ish; most photos are 3:4 or 9:16)
      var img = t.image, ia = img && img.width ? img.width / img.height : 0.75, ca = (CW - 0.16) / (CH - 0.16);
      t.matrixAutoUpdate = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      if (ia > ca) { var sx = ca / ia; t.matrix.setUvTransform((1 - sx) / 2, 0, sx, 1, 0, 0, 0); }
      else { var sy = ia / ca; t.matrix.setUvTransform(0, (1 - sy) / 2, 1, sy, 0, 0, 0); }
    }
    texFor(0, function (t) { fitTexture(t); photoMat.map = t; photoMat.needsUpdate = true; if (hasGsap) g.to(photoMat, { opacity: 1, duration: 0.8 }); else photoMat.opacity = 1; });
    looks.forEach(function (_, i) { if (i) texFor(i, fitTexture); });

    // Gold dust
    var N = isMobile ? 220 : 520, pos = new Float32Array(N * 3), vel = new Float32Array(N * 2);
    var SX = 9, SY = 7, SZ = 5;
    for (var i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * SX; pos[i * 3 + 1] = (Math.random() - 0.5) * SY; pos[i * 3 + 2] = (Math.random() - 0.5) * SZ;
      vel[i * 2] = 0.002 + Math.random() * 0.005; vel[i * 2 + 1] = Math.random() * Math.PI * 2;
    }
    var dustGeo = new THREE.BufferGeometry(); dustGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    var sc = doc.createElement('canvas'); sc.width = sc.height = 64; var sx2 = sc.getContext('2d');
    var grd = sx2.createRadialGradient(32, 32, 1, 32, 32, 14); grd.addColorStop(0, 'rgba(255,244,214,1)'); grd.addColorStop(0.5, 'rgba(201,174,125,0.8)'); grd.addColorStop(1, 'rgba(201,174,125,0)');
    sx2.fillStyle = grd; sx2.fillRect(0, 0, 64, 64);
    var dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({ size: isMobile ? 0.09 : 0.11, map: new THREE.CanvasTexture(sc), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0.9, sizeAttenuation: true }));
    scene.add(dust);

    // State
    var st = { rotY: 0, dragY: 0, px: 0, py: 0, running: true, visible: true, t: 0, idx: 0, busy: false };
    card.scale.setScalar(0.001);
    hero.classList.add('md-hero--3d');
    var chip = hero.querySelector('[data-md-chip]'), dots = hero.querySelectorAll('[data-md-dot]');

    function resize() {
      var w = stage.clientWidth || 600, h = stage.clientHeight || w;
      renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    }
    resize();
    if (window.ResizeObserver) new ResizeObserver(resize).observe(stage); else window.addEventListener('resize', resize);

    function setLook(i) {
      st.idx = (i + looks.length) % looks.length;
      var L = looks[st.idx];
      if (chip) { chip.textContent = L.title + ' · ' + L.price; chip.setAttribute('href', L.url); }
      dots.forEach(function (d, k) { if (k === st.idx) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
    }
    function goLook(i) {
      if (st.busy || looks.length < 2) return;
      var target = (i + looks.length) % looks.length; if (target === st.idx) return;
      st.busy = true;
      texFor(target, function (t) {
        var swap = function () { photoMat.map = t; photoMat.needsUpdate = true; setLook(target); };
        if (hasGsap) {
          g.timeline({ onComplete: function () { st.busy = false; } })
            .to(st, { dragY: st.dragY + Math.PI / 2, duration: 0.45, ease: 'power2.in' })
            .add(swap)
            .to(st, { dragY: st.dragY + Math.PI, duration: 0.6, ease: 'power3.out' });
        } else { swap(); st.busy = false; }
      });
    }
    dots.forEach(function (d) { d.addEventListener('click', function () { goLook(parseInt(d.getAttribute('data-md-dot'), 10)); }); });

    // Intro
    if (hasGsap) {
      var sObj = { s: 0.001 };
      g.timeline()
        .to(silkMat.uniforms.uOpacity, { value: 0.85, duration: 1.4, ease: 'power2.out' }, 0)
        .to(sObj, { s: 1, duration: 1.1, ease: 'back.out(1.4)', onUpdate: function () { card.scale.setScalar(sObj.s); } }, 0.25);
    } else { card.scale.setScalar(1); silkMat.uniforms.uOpacity.value = 0.85; }

    // Pointer: parallax, drag to tilt, tap for the next look
    var dragging = false, moved = 0, lastX = 0;
    window.addEventListener('pointermove', function (e) {
      st.px = (e.clientX / window.innerWidth - 0.5) * 2; st.py = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });
    canvas.addEventListener('pointerdown', function (e) { dragging = true; moved = 0; lastX = e.clientX; if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging) return; var dx = e.clientX - lastX; lastX = e.clientX; moved += Math.abs(dx); st.dragY += dx * 0.01;
    });
    function up() { if (!dragging) return; dragging = false; if (moved < 6) goLook(st.idx + 1); }
    canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);

    // Pause when off-screen or tab hidden
    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { st.visible = en[0].isIntersecting; }, { threshold: 0.25 }).observe(stage);
    doc.addEventListener('visibilitychange', function () { st.running = !doc.hidden; });

    var clock = new THREE.Clock();
    function frame() {
      requestAnimationFrame(frame);
      if (!st.running || !st.visible) return;
      var dt = Math.min(clock.getDelta(), 0.05); st.t += dt;
      silkMat.uniforms.uTime.value = st.t;
      var p = dustGeo.attributes.position.array;
      for (var i = 0; i < N; i++) {
        var yi = i * 3 + 1; p[yi] += vel[i * 2] * (60 * dt); p[i * 3] += Math.sin(st.t * 0.6 + vel[i * 2 + 1]) * 0.0025 * (60 * dt);
        if (p[yi] > SY / 2) { p[yi] = -SY / 2; p[i * 3] = (Math.random() - 0.5) * SX; }
      }
      dustGeo.attributes.position.needsUpdate = true;
      // Card: settle toward the drag angle (snapped to a face), pointer tilt, idle float
      var snap = Math.round(st.dragY / Math.PI) * Math.PI;
      if (!dragging && !st.busy) st.dragY += (snap - st.dragY) * 0.06;
      st.rotY += (st.dragY + st.px * 0.18 - st.rotY) * 0.1;
      card.rotation.y = st.rotY;
      card.rotation.x = st.py * -0.05 + Math.sin(st.t * 0.9) * 0.01;
      card.position.y = 0.05 + Math.sin(st.t * 1.1) * 0.05;
      silk.rotation.y = -0.22 + st.px * 0.04;
      camera.position.x += ((st.px * 0.35) - camera.position.x) * 0.05;
      camera.position.y += ((0.3 + st.py * -0.2) - camera.position.y) * 0.05;
      camera.lookAt(0, 0.1, 0);
      renderer.render(scene, camera);
    }
    frame();
  }

  function initHero() {
    doc.querySelectorAll('[data-md-hero]').forEach(function (hero) {
      if (hero.hasAttribute('data-md-ready')) return;
      hero.setAttribute('data-md-ready', '');
      runHeroCopy(hero);
      if (reduced) return;
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); initHero3D(hero); } }, { rootMargin: '200px' });
        io.observe(hero);
      } else initHero3D(hero);
    });
  }

  function boot() {
    initHero(); runRunway(); runReveals(); runRings(); runTilt();
    if (hasGsap) { setTimeout(function () { ST.refresh(); }, 600); window.addEventListener('load', function () { ST.refresh(); }); if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(function () { ST.refresh(); }); }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  doc.addEventListener('shopify:section:load', function () { runReveals(); initHero(); runRunway(); if (hasGsap) ST.refresh(); });
})();
